import type { DocMeta } from '@blocksuite/store';
import {
  IconButton,
  Modal,
  PiqueLogo,
  Scrollable,
  toast,
  useConfirmModal,
} from '@pique/component';
import { useFilteredPageMetas } from '@pique/core/components/page-list/use-filtered-page-metas';
import { useBlockSuiteMetaHelper } from '@pique/core/hooks/pique/use-block-suite-meta-helper';
import { useBlockSuiteDocMeta } from '@pique/core/hooks/use-block-suite-page-meta';
import { useI18n } from '@pique/i18n';
import { useService, WorkspaceService } from '@toeverything/infra';
import { ArrowTurnBackwardIcon, Delete02Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useCallback, useMemo } from 'react';

import * as styles from './style.css';

interface TrashModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const formatDate = (timestamp: number | undefined): string => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const TrashItem = ({
  item,
  onRestore,
  onDelete,
}: {
  item: DocMeta;
  onRestore: (id: string, title: string) => void;
  onDelete: (id: string, title: string) => void;
}) => {
  const title = item.title || 'Untitled';

  return (
    <div className={styles.trashItem}>
      <div className={styles.trashItemInfo}>
        <div className={styles.trashItemTitle}>{title}</div>
        <div className={styles.trashItemDate}>
          {item.trashDate ? `Deleted ${formatDate(item.trashDate)}` : ''}
        </div>
      </div>
      <div className={styles.trashItemActions}>
        <IconButton
          size="20"
          onClick={() => onRestore(item.id, title)}
          tooltip="Restore"
        >
          <HugeiconsIcon
            icon={ArrowTurnBackwardIcon}
            size={16}
            style={{ transform: 'scaleY(-1)' }}
          />
        </IconButton>
        <IconButton
          size="20"
          onClick={() => onDelete(item.id, title)}
          tooltip="Delete permanently"
        >
          <HugeiconsIcon icon={Delete02Icon} size={16} />
        </IconButton>
      </div>
    </div>
  );
};

export const TrashModal = ({ open, onOpenChange }: TrashModalProps) => {
  const t = useI18n();
  const currentWorkspace = useService(WorkspaceService).workspace;
  const docCollection = currentWorkspace.docCollection;
  const { openConfirmModal } = useConfirmModal();

  const { restoreFromTrash, permanentlyDeletePage } =
    useBlockSuiteMetaHelper(docCollection);

  const pageMetas = useBlockSuiteDocMeta(docCollection);
  const trashedItems = useFilteredPageMetas(pageMetas, { trash: true });

  const sortedItems = useMemo(() => {
    return [...trashedItems].sort((a, b) => {
      const dateA = a.trashDate || 0;
      const dateB = b.trashDate || 0;
      return dateB - dateA;
    });
  }, [trashedItems]);

  const handleRestore = useCallback(
    (id: string, title: string) => {
      restoreFromTrash(id);
      toast(t['com.pique.toastMessage.restored']({ title }));
    },
    [restoreFromTrash, t]
  );

  const handleDelete = useCallback(
    (id: string, _title: string) => {
      openConfirmModal({
        title: t['com.pique.trashOperation.deletePermanently'](),
        description: t['com.pique.trashOperation.deleteDescription'](),
        cancelText: t['Cancel'](),
        confirmText: t['com.pique.trashOperation.delete'](),
        confirmButtonOptions: {
          variant: 'error',
        },
        onConfirm: () => {
          permanentlyDeletePage(id);
          toast(t['com.pique.toastMessage.permanentlyDeleted']());
        },
      });
    },
    [openConfirmModal, permanentlyDeletePage, t]
  );

  const handleDeleteAll = useCallback(() => {
    if (sortedItems.length === 0) return;
    const count = sortedItems.length;

    openConfirmModal({
      title: `Delete all ${count} items permanently`,
      description: `This will permanently delete ${count} item${count !== 1 ? 's' : ''}. This action cannot be undone.`,
      cancelText: t['Cancel'](),
      confirmText: t['com.pique.trashOperation.delete'](),
      confirmButtonOptions: {
        variant: 'error',
      },
      onConfirm: () => {
        const itemIds = sortedItems.map(item => item.id);
        itemIds.forEach(id => permanentlyDeletePage(id));
        toast(`Permanently deleted ${itemIds.length} items`);
      },
    });
  }, [openConfirmModal, permanentlyDeletePage, sortedItems, t]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      width={480}
      height={560}
      contentOptions={{
        style: {
          padding: '24px 0 0 0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <div className={styles.trashModalContent}>
        <div className={styles.trashHeader}>
          <div className={styles.trashHeaderRow}>
            <div className={styles.trashTitle}>
              {t['com.pique.workspaceSubPath.trash']()}
            </div>
            {sortedItems.length > 0 && (
              <button
                className={styles.deleteAllButton}
                onClick={handleDeleteAll}
              >
                Delete all
              </button>
            )}
          </div>
          <div className={styles.trashSubtitle}>
            {sortedItems.length > 0
              ? `${sortedItems.length} item${sortedItems.length !== 1 ? 's' : ''} in trash`
              : 'No items in trash'}
          </div>
        </div>

        {sortedItems.length > 0 ? (
          <Scrollable.Root className={styles.trashList}>
            <Scrollable.Viewport className={styles.trashListViewport}>
              {sortedItems.map(item => (
                <TrashItem
                  key={item.id}
                  item={item}
                  onRestore={handleRestore}
                  onDelete={handleDelete}
                />
              ))}
            </Scrollable.Viewport>
            <Scrollable.Scrollbar />
          </Scrollable.Root>
        ) : (
          <div className={styles.emptyState}>
            <PiqueLogo className={styles.emptyLogo} />
            <span className={styles.emptyText}>
              {t['com.pique.workspaceSubPath.trash.empty-description']()}
            </span>
          </div>
        )}
      </div>
    </Modal>
  );
};
